/**
 * Admin dashboard queries — aggregate stats, user counts, recent users,
 * signup trend, finance summary, and chat analytics.
 *
 * User aggregates flow through the SECURITY DEFINER admin_dashboard_summary
 * RPC instead of client-side counting over admin_get_all_users. The three
 * functions that genuinely need the full user list (fetchRecentUsers,
 * fetchAllUsersRPC, fetchSignupDates) still call admin_get_all_users.
 */
import { supabase } from '@/lib/supabase'
import { invokeEdgeJson } from '../_edge'
import { ADMIN_FINANCE } from '../_endpoints'
import { ADMIN_CHAT_ANALYTICS_LIMIT } from '../_constants'

/** Fetch dashboard summary counts.
 *
 *  User aggregates come from the SECURITY DEFINER admin_dashboard_summary()
 *  RPC — a single row with 5 scalar counts, so we don't ship the full user
 *  list to the client just to count things. `today_messages` still derives
 *  from a small chat_analytics scan (filtered by today's date). */
export async function fetchDashboardStats() {
  const today = new Date().toISOString().split('T')[0]
  const [chatRes, summaryRes] = await Promise.all([
    supabase.from('chat_analytics').select('message_count').eq('date', today),
    supabase.rpc('admin_dashboard_summary'),
  ])
  if (chatRes.error) throw chatRes.error
  if (summaryRes.error) throw summaryRes.error
  const summary = summaryRes.data?.[0] || {}
  return {
    betaTesters: Number(summary.beta_testers) || 0,
    totalUsers: Number(summary.total_users) || 0,
    todayMessages: chatRes.data?.reduce((sum, r) => sum + (r.message_count || 0), 0) || 0,
    todaySignups: Number(summary.today_signups) || 0,
    dailyActiveUsers: Number(summary.daily_active_users) || 0,
  }
}

/** Fetch total and onboarded user counts. Uses the SECURITY DEFINER
 *  admin_dashboard_summary RPC — returns two scalars instead of shipping the
 *  full user list just to count. */
export async function fetchUserCounts() {
  const { data, error } = await supabase.rpc('admin_dashboard_summary')
  if (error) throw error
  const summary = data?.[0] || {}
  return {
    total: Number(summary.total_users) || 0,
    onboarded: Number(summary.onboarded_users) || 0,
  }
}

/** Fetch recent users for dashboard listing. */
export async function fetchRecentUsers() {
  // profiles has no created_at — use RPC which joins auth.users for joined_at
  const { data, error } = await supabase.rpc('admin_get_all_users')
  if (error) throw error
  return (data || []).map(u => ({
    id: u.id,
    display_name: u.display_name,
    is_premium: u.is_premium,
    created_at: u.joined_at, // mapped from auth.users.created_at
  }))
}

/** Fetch signup dates for chart rendering. */
export async function fetchSignupDates() {
  // profiles has no created_at — use RPC which joins auth.users for joined_at
  const { data, error } = await supabase.rpc('admin_get_all_users')
  if (error) throw error
  return (data || []).map(u => ({ created_at: u.joined_at }))
}

/** Fetch all users via admin RPC (for analytics enrichment). */
export async function fetchAllUsersRPC() {
  const { data, error } = await supabase.rpc('admin_get_all_users')
  if (error) throw error
  return data || []
}

/** Call admin-finance edge function for Stripe revenue data. */
export async function fetchFinanceData() {
  return invokeEdgeJson(ADMIN_FINANCE, {}, { errorMessage: 'Finance fetch failed' })
}

/** Load chat analytics rows (paginated). Default limit chosen to preserve
 *  current unbounded-caller behaviour at pre-launch scale; callers can pass
 *  their own `{ limit, offset }` to page through. */
export async function fetchChatAnalytics({ limit = ADMIN_CHAT_ANALYTICS_LIMIT, offset = 0 } = {}) {
  const { data, error } = await supabase
    .from('chat_analytics')
    .select('date, tab, message_count, user_id')
    .range(offset, offset + limit - 1)
  if (error) throw error
  return data || []
}

/** Load chat analytics with date/tab/count columns. */
export async function fetchChatAnalyticsSummary() {
  const { data, error } = await supabase.from('chat_analytics').select('date, tab, message_count')
  if (error) throw error
  return data || []
}
